import React, { createContext, useState, ReactNode, useEffect, useRef } from 'react';
import { Role, User } from '../types';
import { isDemoMode, logEnvironmentStatus } from '../utils/environment';
import { SupabaseAuthService } from '../services/supabaseService';
import { AuthService } from '../services/dataService';
import logger from '../utils/logger';
import { mapDatabaseRoleToEnum } from '../utils/authHelpers';

export interface AuthContextType {
  user: User | null;
  login: (role: Role) => Promise<void>;
  loginDemo: (role: Role) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signUp?: (
    email: string,
    password: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string; // super_adminの場合undefinedを許可
      landTransporterId?: string;
    }
  ) => Promise<void>;
  resetPassword?: (email: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setIsLocalStorageAuthcompleted] = useState(false);
  const [authService] = useState<AuthService>(() => {
    // FL-104: 統一されたSupabaseAuthServiceを使用（デモモード対応済み）
    return new SupabaseAuthService() as AuthService;
  });

  // unsubscribe関数をuseRefで永続管理（useEffect再実行時も保持）
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // 初期化時に環境状況をログ出力し、認証状態を確認
  useEffect(() => {
    let isInitialized = false;

    const initializeAuth = async () => {
      if (isInitialized) return; // 重複初期化防止

      logEnvironmentStatus();

      try {
        // FL-104: デモモード対応 - localStorage認証を使用せず、直接Supabaseサービス利用
        if (isDemoMode()) {
          logger.debug('🎭 AuthContext: デモモード - Supabaseサービス直接利用');
          // デモモード時は認証状態なしで開始（ログイン画面表示）
          setUser(null);
          setLoading(false); // デモモード時は即座にローディング終了
        } else {
          // 本番モード時のみlocalStorage認証確認を実行
          const AUTH_STORAGE_KEY = 'fleetpilot_mock_current_user';
          const storedUserData = localStorage.getItem(AUTH_STORAGE_KEY);

          if (storedUserData) {
            try {
              const rawUser = JSON.parse(storedUserData);
              if (rawUser && rawUser.name && rawUser.role) {
                // LocalStorageのrole値をマッピング
                const mappedUser: User = {
                  ...rawUser,
                  role: mapDatabaseRoleToEnum(rawUser.role),
                };

                logger.debug('✅ AuthContext: localStorage認証状態復元', {
                  name: mappedUser.name,
                  originalRole: rawUser.role,
                  mappedRole: mappedUser.role,
                });
                setUser(mappedUser);
                setIsLocalStorageAuthcompleted(true); // localStorage認証完了フラグ

                // 🔧 最適化修正: localStorage認証がある場合、Supabaseセッション確認をスキップして高速化
                logger.debug(
                  '⚡ AuthContext: localStorage認証で即座にローディング終了（Supabase確認スキップ）'
                );
                setLoading(false); // 即座にローディング終了

                // バックグラウンドでSupabaseセッション検証を実行（非同期、UI遅延なし）
                authService
                  .getCurrentUser()
                  .then(currentUser => {
                    if (currentUser) {
                      logger.debug('✅ AuthContext: Supabaseセッション検証完了', {
                        name: currentUser.name,
                        userId: currentUser.id,
                      });
                      setUser(currentUser); // Supabaseセッションのユーザー情報で上書き（信頼性向上）
                    }
                  })
                  .catch(supabaseError => {
                    logger.warn(
                      '⚠️ AuthContext: Supabaseセッション検証エラー（バックグラウンド）',
                      {
                        error: supabaseError,
                      }
                    );
                    // localStorageユーザーのままで継続
                  });

                return;
              }
            } catch (parseError) {
              logger.warn('⚠️ AuthContext: localStorage認証データ解析失敗:', { error: parseError });
              localStorage.removeItem(AUTH_STORAGE_KEY);
            }
          }

          // localStorage未存在時のSupabase認証確認
          logger.debug('🔍 AuthContext: Supabase認証状態確認');
          try {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
          } catch {
            logger.debug('ℹ️ AuthContext: Supabase認証なし（ログイン画面表示）');
            setUser(null);
          }
          setLoading(false); // 認証確認完了時はローディング終了
        }
      } catch (error) {
        logger.error('❌ AuthContext: 認証初期化エラー:', { error });
        setUser(null);
        setLoading(false); // エラー時も必ずローディング終了
      } finally {
        isInitialized = true;
      }
    };

    // 既存のリスナーが存在する場合は先に停止（重複登録防止）
    if (unsubscribeRef.current) {
      logger.debug('🔄 AuthContext: 既存Supabaseリスナーを停止して再設定');
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // 初期化実行
    initializeAuth();

    // FL-104: デモモード時はリスナー設定をスキップ
    if (isDemoMode()) {
      logger.debug('🎭 AuthContext: デモモード - Supabaseリスナー設定をスキップ');
      return;
    }

    // localStorage認証チェック（本番モードのみ）
    const AUTH_STORAGE_KEY = 'fleetpilot_mock_current_user';
    const storedUserData = localStorage.getItem(AUTH_STORAGE_KEY);
    let hasLocalStorageAuth = false;

    if (storedUserData) {
      try {
        const localUser: User = JSON.parse(storedUserData);
        if (localUser && localUser.name && localUser.role) {
          hasLocalStorageAuth = true;
          logger.debug('✅ AuthContext: localStorage認証確認済み - Supabaseリスナー設定をスキップ');
        }
      } catch (error) {
        logger.warn('⚠️ AuthContext: localStorage認証データ確認失敗:', { error });
      }
    }

    // 🔧 修正: localStorage認証がある場合も、Supabaseセッション変更を監視
    // Supabaseセッションが失効したり、ログアウトされた場合に対応するため
    if (authService.onAuthStateChange) {
      if (hasLocalStorageAuth) {
        logger.debug(
          '🔗 AuthContext: Supabaseリスナー設定（localStorage認証あり、セッション変更監視）'
        );
      } else {
        logger.debug('🔗 AuthContext: Supabaseリスナー設定（localStorage認証なし）');
      }
      unsubscribeRef.current = authService.onAuthStateChange(user => {
        if (isInitialized) {
          logger.debug('🔄 AuthContext: Auth state change detected:', {
            user: user ? user.name : 'null',
          });
          setUser(user);
          setLoading(false); // リスナー経由の認証状態変更時もローディング終了
        }
      });
    } else {
      logger.debug('ℹ️ AuthContext: Supabase未利用 - リスナー設定をスキップ');
    }

    // クリーンアップ関数
    return () => {
      if (unsubscribeRef.current) {
        logger.debug('🧹 AuthContext: クリーンアップ - Supabaseリスナー停止');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []); // 依存配列を空にして無限ループを防止（認証サービスは初期化時に1回だけ設定）

  // 役割選択ログイン（モックモード・Supabase未設定時のデモ機能対応）
  const login = async (role: Role): Promise<void> => {
    try {
      setIsLocalStorageAuthcompleted(false); // ユーザーアクション時はフラグリセット
      setLoading(true);
      const loggedInUser = await authService.loginWithRole(role);
      setUser(loggedInUser);
    } catch (error) {
      logger.error('Login failed:', { error });
      throw error; // LoginPageでエラーハンドリングするために再スロー
    } finally {
      setLoading(false);
    }
  };

  // デモ専用ログイン（環境変数に関係なくデモ認証を実行）
  const loginDemo = async (role: Role): Promise<void> => {
    try {
      setIsLocalStorageAuthcompleted(false);
      setLoading(true);

      // デモ認証用の実際のSupabaseユーザー情報
      // SUPER_ADMINは実際のSupabaseアカウントを使用するため、デモ対象外
      const demoAuthUsers = {
        [Role.ADMIN]: {
          email: 'admin@sample-rikuso.co.jp',
          password: 'demo-password-2024',
          name: 'サンプル管理者',
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259', // デモ会社ID統一
          landTransporterId: undefined,
        },
        [Role.DISPATCHER]: {
          email: 'dispatcher@sample-rikuso.co.jp',
          password: 'demo-password-2024',
          name: 'サンプル配車担当',
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
          landTransporterId: undefined,
        },
        [Role.DRIVER]: {
          email: 'driver@sample-rikuso.co.jp',
          password: 'demo-password-2024',
          name: 'サンプル陸送員A',
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
          landTransporterId: '0a00ad35-df7c-4347-a02e-0b7b378d83e3', // サンプル陸送員AのID
        },
        [Role.MANAGER]: {
          email: 'manager@sample-rikuso.co.jp',
          password: 'demo-password-2024',
          name: 'サンプルマネージャー',
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
          landTransporterId: undefined,
        },
        [Role.LAND_TRANSPORTER]: {
          email: 'land-transporter@sample-rikuso.co.jp',
          password: 'demo-password-2024',
          name: 'サンプル陸送員A',
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
          landTransporterId: '0a00ad35-df7c-4347-a02e-0b7b378d83e3', // サンプル陸送員AのID
        },
        [Role.READ_ONLY]: {
          email: 'readonly@sample-rikuso.co.jp',
          password: 'demo-password-2024',
          name: 'サンプル閲覧者',
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
          landTransporterId: undefined,
        },
      };

      // SUPER_ADMINはデモログイン対象外
      if (role === Role.SUPER_ADMIN) {
        throw new Error(
          'ラクハイ管理者は通常のログイン画面から実際のアカウントでログインしてください。'
        );
      }

      const demoAuthUser = demoAuthUsers[role];
      if (!demoAuthUser) {
        throw new Error(`サポートされていないロール: ${role}`);
      }

      logger.debug('🎭 デモモード: 実際のSupabase認証を実行中...', {
        email: demoAuthUser.email,
        role,
      });

      // 実際のSupabase認証を実行（デモ用固定アカウント）
      try {
        logger.debug('🎭 デモモード: 実際のSupabase認証を実行中...');

        // Supabaseクライアントを取得
        const { getSupabaseClient } = await import('../utils/supabase');
        const supabaseClient = getSupabaseClient();

        if (supabaseClient) {
          // 実際のデモユーザーでログイン
          const { data: authData, error: authError } = await supabaseClient.auth.signInWithPassword(
            {
              email: demoAuthUser.email, // 各ロールに応じた正しいデモユーザーのメール
              password: demoAuthUser.password, // 各ロールに応じた正しいデモユーザーのパスワード
            }
          );

          if (authError) {
            logger.warn('⚠️ Supabase認証失敗:', { error: authError });
            throw authError;
          }

          if (authData.user) {
            // 認証が成功した場合、デモユーザー情報と組み合わせ
            const enhancedUser: User = {
              id: authData.user.id, // 実際のSupabaseユーザーID
              email: demoAuthUser.email, // デモ表示用のメール
              name: demoAuthUser.name,
              role,
              company: demoAuthUser.company,
              companyId: demoAuthUser.companyId,
              landTransporterId: demoAuthUser.landTransporterId,
            };

            logger.debug('✅ デモログイン成功（Supabase認証済み）:', {
              role,
              name: enhancedUser.name,
              companyId: enhancedUser.companyId,
              supabaseUserId: authData.user.id,
              actualEmail: authData.user.email,
            });

            // デモユーザー情報をlocalStorageに保存（SupabaseMasterDataServiceから参照用）
            localStorage.setItem('fleetpilot_demo_current_user', JSON.stringify(enhancedUser));

            setUser(enhancedUser);
          } else {
            throw new Error('認証に成功しましたが、ユーザーデータが取得できませんでした');
          }
        } else {
          throw new Error('Supabaseクライアントが利用できません');
        }
      } catch (supabaseAuthError) {
        logger.warn('⚠️ Supabase認証失敗、フォールバック認証を使用:', { error: supabaseAuthError });

        // Supabase認証が失敗した場合のフォールバック（従来の方式）
        const fallbackUser: User = {
          id: `demo-${role}-${Date.now()}`,
          email: demoAuthUser.email,
          name: demoAuthUser.name,
          role,
          company: demoAuthUser.company,
          companyId: demoAuthUser.companyId,
          landTransporterId: demoAuthUser.landTransporterId,
        };

        logger.debug('🎭 デモログイン（フォールバック）:', {
          role,
          name: fallbackUser.name,
          companyId: fallbackUser.companyId,
        });

        // フォールバックユーザー情報をlocalStorageに保存
        localStorage.setItem('fleetpilot_demo_current_user', JSON.stringify(fallbackUser));

        setUser(fallbackUser);
      }
    } catch (error) {
      logger.error('Demo login failed:', { error });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * メール・パスワードログイン（全モード対応）
   * @param email メールアドレス
   * @param password パスワード
   * @throws 認証失敗時にエラーをスロー
   */
  const loginWithEmail = async (email: string, password: string) => {
    setIsLocalStorageAuthcompleted(false); // ユーザーアクション時はフラグリセット
    setLoading(true);
    try {
      const loggedInUser = await authService.loginWithEmail(email, password);
      setUser(loggedInUser);
    } catch (error) {
      logger.error('Email login failed:', { error });
      throw error; // LoginPageでエラーハンドリングするために再スロー
    } finally {
      setLoading(false);
    }
  };

  // デバッグ用: データベース状況確認
  const debugDatabase = async () => {
    if (isDemoMode()) {
      logger.debug('🔍 デモモードのため、デバッグは利用できません');
      return;
    }
    logger.debug('🔍 データベースデバッグ機能は実装中です');
  };

  // ユーザー登録（本番モードのみ）
  const signUp = async (
    email: string,
    password: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string; // super_adminの場合undefinedを許可
      landTransporterId?: string;
    }
  ) => {
    if (isDemoMode()) {
      throw new Error('ユーザー登録は本番モードでのみ利用可能です');
    }

    if (!authService.signUp) {
      throw new Error('signUp機能が利用できません');
    }

    setIsLocalStorageAuthcompleted(false); // ユーザーアクション時はフラグリセット
    setLoading(true);
    try {
      const newUser = await authService.signUp(email, password, userData);
      setUser(newUser);
    } catch (error) {
      logger.error('SignUp failed:', { error });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // パスワードリセット（本番モードのみ）
  const resetPassword = async (email: string) => {
    if (isDemoMode()) {
      throw new Error('パスワードリセットは本番モードでのみ利用可能です');
    }

    if (!authService.resetPassword) {
      throw new Error('resetPassword機能が利用できません');
    }

    try {
      await authService.resetPassword(email);
    } catch (error) {
      logger.error('Password reset failed:', { error });
      throw error;
    }
  };

  const logout = async () => {
    try {
      setIsLocalStorageAuthcompleted(false); // ログアウト時はフラグリセット
      setLoading(true);
      await authService.logout();

      // デモユーザー情報もクリア
      localStorage.removeItem('fleetpilot_demo_current_user');

      setUser(null);
    } catch (error) {
      logger.error('Logout failed:', { error });
    } finally {
      setLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    user,
    login,
    loginDemo,
    loginWithEmail,
    signUp: !isDemoMode() ? signUp : undefined,
    resetPassword: !isDemoMode() ? resetPassword : undefined,
    logout,
    loading,
  };

  // デバッグ用: グローバルにdebugDatabase関数を公開
  if (typeof window !== 'undefined' && !isDemoMode()) {
    (window as any).debugDatabase = debugDatabase;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
